import { useCallback, useContext, useState } from 'react';

import { Groups2, MeetingRoom, Person2 } from '@mui/icons-material';
import { List, ListItem, ListItemIcon, ListItemText, styled, Typography } from '@mui/material';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useContainer } from 'unstated-next';

import { SubClassContainer } from './SubClassContainer';
import { CLASS_DELETE } from '../../../config/apiPaths';
import { ClassContext } from '../../../context/ClassContext';
import { type SubClassRecord } from '../../../context/types';
import { getUserUuidHeaders } from '../../../utils/httpUtils';
import { processApiPath } from '../../../utils/url';
import { type ConfirmationDialogProps } from '../../common/dialog/ConfirmationDialog';
import { DeleteConfirmationDialog } from '../../common/dialog/DeleteConfirmationDialog';

export type DeleteSubClassDialogProps<T> = Omit<ConfirmationDialogProps<T>, 'onConfirm' | 'open'> & {
    deleteConfirmationClass: Partial<SubClassRecord> | null;
};

const ListItemIconStyled = styled(ListItemIcon)`
    min-width: 3rem;
`;

export const SubClassesDeleteDialog = <T extends object>(props: DeleteSubClassDialogProps<T>) => {
    const { deleteConfirmationClass, onClose } = props;
    const { t } = useTranslation('class');
    const [isDeleting, setIsDeleting] = useState(false);

    const { userUuid } = useContext(ClassContext);
    const { enqueueSnackbar } = useSnackbar();
    const { fetchSubClasses, mainClass } = useContainer(SubClassContainer);
    const handleClose = useCallback(() => {
        if (!isDeleting) {
            onClose?.({}, 'backdropClick');
        }
    }, [isDeleting, onClose]);

    const handleClassDelete = async () => {
        try {
            setIsDeleting(true);
            const url = processApiPath(CLASS_DELETE, {}, { meeting_id: deleteConfirmationClass!.meeting_id! });
            await axios.delete(url, { headers: getUserUuidHeaders(userUuid) });
            await fetchSubClasses(true);
        } catch (err) {
            console.log(err);
            if (axios.isAxiosError(err) && err.response?.status === 404) {
                enqueueSnackbar(t('class-not-found'), { variant: 'warning' });
                await fetchSubClasses(true);
            } else {
                enqueueSnackbar(t('error-delete'), { variant: 'error' });
            }
        }
        setIsDeleting(false);
        onClose?.({}, 'backdropClick');
    };

    return (
        <DeleteConfirmationDialog
            title={t('sub-class.delete-title')}
            {...props}
            open={Boolean(deleteConfirmationClass)}
            onClose={handleClose}
            onConfirm={handleClassDelete}
        >
            <Typography variant="body1">{t('sub-class.delete-confirm-message')}</Typography>
            <List>
                <ListItem key={`subclass-delete-row-class-name`} disablePadding>
                    <ListItemIconStyled key={'subclass-delete-class-name-icon'}>
                        <MeetingRoom fontSize="large" />
                    </ListItemIconStyled>
                    <ListItemText
                        primary={deleteConfirmationClass?.class_name}
                        secondary={deleteConfirmationClass?.class_description || t('name')}
                        key={'subclass-delete-class-name-text'}
                    />
                </ListItem>
                {deleteConfirmationClass?.leadInstructor && (
                    <ListItem key={`subclass-delete-row-host`} disablePadding>
                        <ListItemIconStyled key={'subclass-delete-host-icon'}>
                            <Person2 fontSize="large" />
                        </ListItemIconStyled>
                        <ListItemText
                            primary={deleteConfirmationClass.leadInstructor.userName}
                            secondary={t('sub-class.host')}
                            key={'subclass-delete-host-text'}
                        />
                    </ListItem>
                )}
                {!!deleteConfirmationClass?.class_group_ids?.length && (
                    <ListItem key={`subclass-delete-row-host`} disablePadding>
                        <ListItemIconStyled key={'subclass-delete-host-icon'}>
                            <Groups2 fontSize="large" />
                        </ListItemIconStyled>
                        <ListItemText
                            primary={
                                deleteConfirmationClass.class_group_ids
                                    ?.map(
                                        (groupId) =>
                                            mainClass?.classGroups?.find(
                                                (fullGroup) => fullGroup.c_student_group_id === groupId
                                            )?.label
                                    )
                                    .filter(Boolean)
                                    .join(', ') || ''
                            }
                            secondary={t('sub-class.groups')}
                            key={'subclass-delete-groups-text'}
                        />
                    </ListItem>
                )}
            </List>
        </DeleteConfirmationDialog>
    );
};
